import { ComponentCO2, ComponentCO2Link, PhasesCO2, SimpleOrganization, User } from '@acies/domain';
import { Observable } from '@acies/observables';
import { IComponentCO2LinkResponseSchema, IComponentCO2LinkSchema, IComponentCO2ResponseSchema, IComponentCO2Schema, IPhasesCO2Schema, IUsers } from '@acies/schema';
import ParseError from '@shared/errors/parse-error';

export function schemaToComponentCO2(schema: IComponentCO2Schema | ParseError, users: Map<string, User>): ComponentCO2 {
	if (schema instanceof ParseError) {
		throw schema;
	}
	const { description, epd, meta, name, phases, self } = schema;
	return new ComponentCO2({
		createdAt: new Date(meta.createdAt),
		createdBy: byToIAM(meta.createdBy, users),
		description,
		epd,
		name,
		phases: schemaToPhasesCO2(phases),
		selected: new Observable(false),
		self,
		updatedAt: new Date(meta.updatedAt),
		updatedBy: byToIAM(meta.updatedBy, users),
		version: meta.version,
	});
}

export function schemaToComponentCO2Link(schema: IComponentCO2LinkSchema | ParseError): ComponentCO2Link {
	if (schema instanceof ParseError) {
		throw schema;
	}
	const { description, name, self, co2 } = schema;
	return new ComponentCO2Link({
		co2: co2.self,
		description,
		name,
		selected: new Observable(false),
		self,
	});
}

export function schemaToComponentCO2LinkList(schema: IComponentCO2LinkResponseSchema): ComponentCO2Link[] {
	if (schema instanceof ParseError) {
		throw schema;
	}
	const items: ComponentCO2Link[] = [];
	for (const item of schema.items) {
		items.push(schemaToComponentCO2Link(item));
	}
	return items;
}

export function schemaToComponentCO2List(schema: IComponentCO2ResponseSchema | ParseError, users: Map<string, User>): ComponentCO2[] {
	if (schema instanceof ParseError) {
		throw schema;
	}
	const items: ComponentCO2[] = [];
	for (const item of schema.items) {
		items.push(schemaToComponentCO2(item, users));
	}
	return items;
}

function byToIAM(value: { self: string } | null, users: Map<string, User>): null | SimpleOrganization | User {
	if (value !== null) {
		if (value.self.startsWith('/iam/users/')) {
			return getUser(value.self, users);
		}
		if (value.self.startsWith('/iam/organizations/')) {
			return getOrganization(value.self);
		}
	}
	return null;
}

function getOrganization(self: string): null | SimpleOrganization {
	const splitArray = self.split('/');
	const account = splitArray[5];
	const id = splitArray[3];
	const name = id;
	const server = splitArray[7];
	if (id && account && server) {
		return new SimpleOrganization({ account, id, name, server });
	}
	return null;
}

function getUser(self: string, users: Map<string, User>): null | User {
	const idx = self.lastIndexOf('/') + 1;
	const id = self.slice(idx);
	const user: undefined | User = users.get(id);
	if (user) {
		return user;
	}
	return null;
}

function schemaToPhasesCO2(schema: IPhasesCO2Schema): PhasesCO2 {
	return new PhasesCO2(schema);
}

export function schemaToUsers(schema: IUsers): Map<string, User> {
	if (schema instanceof ParseError) {
		throw schema;
	}
	const users = new Map<string, User>();
	for (const schemaUser of schema) {
		const { createdAt, createdBy, defaultAccount, email, grantType, id, isFederated, permissionCodes, status, updatedAt, version, name, updatedBy } = schemaUser;
		const user = new User({
			createdAt: new Date(createdAt),
			createdBy,
			defaultAccount,
			email,
			grantType,
			id,
			isFederated,
			permissionCodes,
			status,
			updatedAt: new Date(updatedAt),
			version,
			name,
			updatedBy,
		});
		users.set(user.id, user);
	}

	// assign instance pointers to created- and updatedBy Users if found
	for (const [_, user] of users) {
		if (typeof user.createdBy === 'string') {
			const createdBy: undefined | User = users.get(user.createdBy);
			if (createdBy) {
				user.createdBy = createdBy;
			}
		}
	}
	return users;
}
